'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowUpRight, Menu, X } from 'lucide-react';

const navLinks = [
  { label: '01 Home', href: '/' },
  { label: '02 Services', href: '/#services' },
  { label: '03 Projects', href: '/projects' },
  { label: '04 Pricing', href: '/pricing' },
  { label: '05 Contact', href: '/#contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Stagger mobile menu open/close
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      // Small delay so overlay mounts before items animate in
      const t = setTimeout(() => setMobileVisible(true), 20);
      return () => clearTimeout(t);
    } else {
      setMobileVisible(false);
      const t = setTimeout(() => {
        document.body.style.overflow = '';
      }, 320);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'nav-glass' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group">
              <AppLogo size={36} />
              <div className="hidden sm:block">
                <div className="text-xs font-semibold text-foreground tracking-widest uppercase leading-none">
                  Selena Ramnarine
                </div>
                <div className="text-label-mono text-muted-foreground mt-0.5">
                  Digital Solutions
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.href}`}
                  href={link?.href}
                  className="text-label-mono text-muted-foreground hover:text-foreground transition-colors duration-200 nav-link-underline"
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* CTA + Hamburger */}
            <div className="flex items-center gap-4">
              <Link
                href="/book-a-consultation"
                className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 border border-foreground/30 text-foreground text-xs font-bold uppercase tracking-widest hover:border-primary hover:bg-primary/10 hover:text-foreground transition-all duration-250 rounded btn-motion"
              >
                Book a Consultation
                <ArrowUpRight size={14} className="btn-arrow" />
              </Link>

              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground hover:text-primary transition-colors"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[100] flex flex-col"
          style={{
            background: 'rgba(10,10,10,0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            opacity: mobileVisible ? 1 : 0,
            transition: 'opacity 280ms cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div className="flex items-center justify-between px-6 h-16">
            <Link href="/" className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
              <AppLogo size={32} />
              <span className="text-sm font-bold tracking-widest uppercase">Selena Ramnarine</span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-foreground hover:text-primary transition-colors"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 flex flex-col justify-center px-8">
            {navLinks?.map((link, i) => (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="py-5 border-b border-border/50 text-3xl font-bold text-foreground hover:text-primary transition-colors duration-200"
                style={{
                  opacity: mobileVisible ? 1 : 0,
                  transform: mobileVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 380ms cubic-bezier(0.22,1,0.36,1) ${80 + i * 55}ms, transform 380ms cubic-bezier(0.22,1,0.36,1) ${80 + i * 55}ms`,
                }}
              >
                {link?.label}
              </Link>
            ))}
          </div>

          <div
            className="px-8 pb-12"
            style={{
              opacity: mobileVisible ? 1 : 0,
              transform: mobileVisible ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 380ms cubic-bezier(0.22,1,0.36,1) ${80 + navLinks?.length * 55}ms, transform 380ms cubic-bezier(0.22,1,0.36,1) ${80 + navLinks?.length * 55}ms`,
            }}
          >
            <Link
              href="/book-a-consultation"
              onClick={() => setMobileOpen(false)}
              className="btn-primary w-full justify-center text-center"
            >
              Book a Consultation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}